/* eslint-disable max-lines -- 756 lines as eslint counts them, above the 600 ceiling (.rules/governance.md); one orchestrator over the seven dev-pipeline stages with checkpoint/resume (hindsight and prior-research recall moved to dev-pipeline-context.ts); split tracked in #6148 */
/**
 * Multi-Agent Development Pipeline (#1684)
 *
 * Orchestrates the full development workflow with iterative loops:
 *
 *   1. RESEARCH — research expert gathers context
 *   2. PLAN+VOTE — architect plans, consensus votes, iterate on feedback
 *   3. DECOMPOSE — PM splits approved plan into phases/epics/issues
 *   4. IMPLEMENT — code experts work assigned tasks in parallel
 *   5. QA REVIEW — QA expert reviews, sends back to PM if issues found
 *   6. SECURITY — SARIF scan blocks on critical/high findings
 *   7. SHIP — all gates passed
 *
 * Each stage can iterate: vote feedback loops back to plan,
 * QA failures loop back to implementation via PM reassignment.
 *
 * @module pipeline/dev-pipeline
 */

import { nexusDataPath } from '../config/nexus-data-dir.js';

import { runQaLoop } from '../orchestration/qa-loop.js';
import {
  type ResearchContext,
  researchContextFromText,
  deriveResearchMaturity,
} from './research-context.js';

import { createLogger, withStep } from '../core/index.js';
import { getPipelineEventBus } from './event-bus.js';
import { createDefaultPolicyEngine } from './policy-engine.js';
import type { PolicyContext } from './policy-engine.js';
import {
  evaluatePipelinePolicy,
  getGateEnforcementMode,
  PolicyBlockedError,
} from './policy-evaluator.js';
import {
  saveStageCheckpoint,
  loadCheckpointState,
  cleanupCheckpoint,
} from './pipeline-checkpoint.js';
import type { PipelineCheckpointState } from './pipeline-checkpoint.js';
import { TraceWriter } from './trace-writer.js';
import { createAuditTrail } from '../security/audit-trail.js';
import type { AuditTrail } from '../security/audit-trail.js';
import { createDurableAuditSink } from '../security/audit-bridge.js';
import type { IAuditLogger } from '../audit/audit-types.js';
import type { IHindsightBeliefMemory } from '../context/belief-memory-interface.js';
import { applyPipelineHindsight, assemblePlanContext } from './dev-pipeline-context.js';
import { DEFAULT_MAX_NO_QUORUM_RETRIES, retryNoQuorumVote } from './iterative-consensus.js';
import { allOf, anyOf } from '../utils/verdict-aggregation.js';

const logger = createLogger({ component: 'dev-pipeline' });

// ============================================================================
// Types
// ============================================================================

/** Agent roles used in the pipeline. */
export type PipelineRole = 'researcher' | 'architect' | 'pm' | 'coder' | 'qa' | 'security';

/** A task decomposed by the PM, potentially with conditional approval requirements. */
export interface PipelineTask {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly assignedTo: PipelineRole;
  readonly status: 'pending' | 'in_progress' | 'review' | 'done' | 'rejected';
  readonly feedback?: string;
  /** Implementation text from the code expert (surfaced for harness use). */
  readonly implementation?: string;
  /** Conditions required for task completion (from conditional_go vote). */
  readonly conditions?: readonly string[] | undefined;
  /** Caveats/warnings associated with the task (from conditional_go vote). */
  readonly caveats?: readonly string[] | undefined;
  /**
   * #3234: deterministic research-maturity `[0,1]` of the run that produced this
   * task, attached after decompose. RECORDED on the routing outcome and measured
   * (the gated live-routing use is #3815). Absent → treated as no-research (0).
   */
  readonly researchMaturity?: number | undefined;
}

/** Vote result from consensus — discriminated union with conditional approval support. */
export type VoteResult =
  | { readonly kind: 'approved'; readonly approvalPercentage: number }
  | { readonly kind: 'rejected'; readonly feedback: string; readonly approvalPercentage: number }
  // NO PRODUCTION PATH PRODUCES THIS, UNDER ANY CONFIGURATION (#5768). The only
  // constructor is `createVoteResult` below, whose sole caller is
  // dev-pipeline.test.ts; every production vote producer builds the literal
  // inline and emits only `approved` / `rejected` (iterative-consensus.ts and
  // agent-executor.ts). FOUR live-looking branches are therefore unreachable:
  // `extractConditionalMeta`'s check, the plan-loop approval check further
  // down this file, and the second disjunct of both `isApproved` here and
  // `isVoteAccepted` in iterative-consensus.ts. `conditions`/`caveats` on a
  // task are always absent.
  //
  // NOT the same kind of inert as `no_quorum` below, and an earlier version of
  // this note said it was. `no_quorum` is inert under DEFAULT policies but
  // genuinely reachable when a caller opts into `absolute_quorum` (#4132) — a
  // real state with a real producer. This one has no producer under any policy.
  //
  // The producer side is half-built rather than absent, which is what makes it
  // look wireable: per-voter `VoteSchema.conditions` exists
  // (consensus/types-core.ts) and `cli/voter-response.ts` preserves it — but
  // nothing in `src/consensus/` ever READS it, and 0 of 234 persisted vote
  // records carry one. Wiring it needs a prompt change plus two governance
  // decisions (how per-voter conditions aggregate; whether a conditional
  // approval clears a supermajority bar), not a missing link.
  //
  // A 7-voter panel took REMOVE, 5 of 6 approvers (#5768). It is published type
  // surface, so the deletion is queued for the next major as #5969 and needs a
  // `unanimous` vote at that point.
  | {
      readonly kind: 'conditional_go';
      readonly conditions: readonly string[];
      readonly caveats: readonly string[];
      readonly approvalPercentage: number;
    }
  // #4135: the vote could not reach a valid quorum — a recoverable "re-run the
  // missing voice" state, DISTINCT from a rejection. Only produced when a caller
  // opts into the `absolute_quorum` error policy (or an error-policy short-circuit
  // voids the vote); inert under every default policy. Consumers must NOT feed this
  // into plan-revision (it carries no reviewer feedback — the plan is fine, a voice
  // was missing); it terminates/escalates instead. `isApproved` and
  // `getVoteFeedback` already treat it as not-approved / no-feedback.
  | { readonly kind: 'no_quorum'; readonly reason: string; readonly approvalPercentage: number };

/**
 * Construct VoteResult from legacy approval flow.
 *
 * NO PRODUCTION CALLER (#5768) — only dev-pipeline.test.ts. Its two siblings
 * `isApproved` and `getVoteFeedback` ARE wired (stage-wrappers.ts:16), which is
 * what makes the asymmetry worth recording: this is the only thing that can
 * emit `conditional_go`, so the variant's branches are unreachable while it
 * stays uncalled.
 */
export function createVoteResult(
  approved: boolean,
  feedback: string,
  approvalPercentage: number,
  conditions?: readonly string[]
): VoteResult {
  if (!approved) {
    return { kind: 'rejected', feedback, approvalPercentage };
  }
  if (conditions !== undefined && conditions.length > 0) {
    return { kind: 'conditional_go', conditions, caveats: [], approvalPercentage };
  }
  return { kind: 'approved', approvalPercentage };
}

/**
 * Check if vote result is approved (either explicit or conditional).
 *
 * The `conditional_go` disjunct cannot be true in production (#5768) — see the
 * note on `VoteResult`. It stays until the variant is removed in #5969, so that
 * the union and its readers go in one change rather than leaving a case the
 * compiler no longer checks.
 */
export function isApproved(result: VoteResult): boolean {
  return result.kind === 'approved' || result.kind === 'conditional_go';
}

/** Get feedback from vote result (only available for rejected). */
export function getVoteFeedback(result: VoteResult): string {
  if (result.kind === 'rejected') return result.feedback;
  return '';
}

/**
 * What portion of the implementation a QA review actually consumed.
 *
 * Lives here rather than in `agent-executor` so the dependency stays one-way
 * (agent-executor imports this module, never the reverse).
 */
export interface QaReviewCoverage {
  /** Characters of the implementation the reviewer was shown. */
  readonly reviewedChars: number;
  /** Characters in the full implementation. */
  readonly totalChars: number;
  /** True when the reviewer saw less than the whole artifact. */
  readonly partial: boolean;
}

/** QA review result. */
export interface QaReviewResult {
  readonly verdict: 'pass' | 'needs_work' | 'reject';
  readonly feedback: string;
  readonly issues: readonly string[];
  /**
   * Portion of the implementation the reviewer actually consumed. ABSENT when
   * the whole artifact was reviewed; present with `partial: true` when the
   * implementation exceeded the prompt budget. Without this the verdict was
   * byte-identical for a 500-char and a 500,000-char implementation (#4140
   * shape, applied to QA).
   */
  readonly coverage?: QaReviewCoverage;
}

/** Overall pipeline result. */
export interface DevPipelineResult {
  /**
   * Whether the pipeline completed successfully.
   *
   * True only when every planned task is present in `tasks` with status 'done'
   * AND the security gate passed (#5645).
   */
  readonly completed: boolean;
  readonly plan: string;
  readonly tasks: readonly PipelineTask[];
  readonly voteIterations: number;
  readonly qaIterations: number;
  /**
   * Whether the security gate passed.
   *
   * Read together with {@link DevPipelineResult.securityRan} (#4772): `false`
   * with `securityRan: false` means no scan produced a measured verdict and is
   * NOT a failed security review.
   */
  readonly securityPassed: boolean;
  /**
   * Whether the security gate actually ran (#4772).
   *
   * Set on every path `runDevPipeline` can return through, so
   * `securityPassed: false` is always readable as verdict-vs-absence. Four
   * paths report `false`: a dry run (plan+vote only), harness mode (tasks are
   * handed back for external implementation), a red quality gate in `blocking`
   * mode, and a security scan that returned `skip`. Only a post-scan `pass` or
   * `fail` verdict reports `true`.
   *
   * Absent means the producer predates the distinction (#4782), not that the
   * scan's status is unknown — treat an absent value as unmeasured, not `false`.
   */
  readonly securityRan?: boolean;
  /** Security-stage feedback explaining why a skipped scan did not run. */
  readonly securityNote?: string;
  /**
   * Terminal planning-gate state. Absent means the panel approved a usable plan.
   * `'empty'` means the planner returned nothing; `'no_quorum'` means the retry
   * budget ended without a valid panel; `'unapproved'` means every permitted
   * revision was rejected. Every present state stops before implementation.
   */
  readonly planStatus?: 'empty' | 'no_quorum' | 'unapproved';
  /** Last quorum failure reason when {@link planStatus} is `'no_quorum'`. */
  readonly planVoteReason?: string;
  /** Last panel approval percentage for a terminal plan-vote outcome. */
  readonly planVoteApprovalPercentage?: number;
  /** Last rejection feedback when {@link planStatus} is `'unapproved'`. */
  readonly planVoteFeedback?: string;
  /**
   * Whether this run stopped after plan+vote because the caller asked it to.
   *
   * `completed: false` alone cannot distinguish "the pipeline failed" from "the
   * pipeline did exactly what was requested and stopped" — and a consumer that
   * reads `completed` as the verdict reports a successful dry run as an engine
   * fault. Absent means a normal run.
   */
  readonly dryRun?: true;
  /**
   * Present only for `mode: 'harness'` runs. Absent means a normal run.
   *
   * The second legitimate `completed: false`, and it had no marker while the
   * dry run had one (#5888). Harness mode hands the tasks back for external
   * implementation and stops on purpose — exactly the distinction
   * {@link DevPipelineResult.dryRun} exists to make, so a consumer reading
   * `completed` as the verdict reported a successful harness run as an engine
   * fault. `run_dev_pipeline`'s async job status is that consumer.
   */
  readonly harnessMode?: true;
  /**
   * Aggregate completion status of planned tasks (#5645).
   *
   * `'all_done'` when every planned task was implemented and passed QA;
   * `'partial'` when some but not all tasks completed with status `'done'`;
   * `'none'` when zero tasks completed with status `'done'` (including empty
   * plans where nothing was planned).
   *
   * Optional so early-return shapes that never ran the implement loop need not
   * carry it.
   */
  readonly taskStatus?: 'all_done' | 'partial' | 'none';
}

// ============================================================================
// Pipeline Stage Interfaces
// ============================================================================

/** Pluggable stage implementations — inject real or mock agents. */
export interface DevPipelineStages {
  /**
   * Research expert gathers context for the task. Returns the full
   * {@link ResearchContext} (#3234 seam 0): `.text` feeds plan/vote as before,
   * `.metadata` is attached to decomposed tasks for routing-experience enrichment.
   */
  research(task: string): Promise<ResearchContext>;
  /** Architect creates a plan from research + task. */
  plan(task: string, research: string, priorFeedback?: string): Promise<string>;
  /**
   * Consensus vote on the plan. Returns approval + feedback. `research` is the
   * research-stage context, surfaced to voters so they can weigh research
   * maturity (#3258) — appended to the proposal as informational, untrusted
   * text (never as instructions).
   */
  vote(plan: string, research: string): Promise<VoteResult>;
  /** PM decomposes approved plan into tasks. */
  decompose(plan: string): Promise<PipelineTask[]>;
  /** Code expert implements a task. Returns the work product. */
  implement(task: PipelineTask): Promise<string>;
  /** QA expert reviews implementation. */
  qaReview(task: PipelineTask, implementation: string): Promise<QaReviewResult>;
  /**
   * Local QA quality gate (typecheck/lint/tests/build) run before ship (#3356).
   * Optional: pipelines that don't supply it simply skip the gate. Returns
   * `passed` plus actionable `feedback` from the underlying `runQualityGate`
   * engine. Whether a red gate fails the phase is governed by the
   * `qualityGate` mode in {@link DevPipelineOptions}, not this method.
   */
  qualityGate?(): Promise<{ passed: boolean; feedback: string }>;
  /**
   * Security scan. `verdict` preserves the scanner's tri-state so a `skip`
   * (scanner absent or errored) is not recorded as a rejection (#5502).
   * Optional so stage implementations that predate the field still satisfy
   * the contract; when absent, `passed` is read as a measured pass/fail.
   */
  securityScan(): Promise<{
    readonly passed: boolean;
    readonly verdict?: 'pass' | 'fail' | 'skip';
    readonly feedback: string;
  }>;
}

// ============================================================================
// Pipeline Execution
// ============================================================================

/** Maximum iterations for each loop. */
const MAX_VOTE_ITERATIONS = 3;
const MAX_QA_ITERATIONS = 3;

/** Resolved loop bounds for one pipeline run (#4939). */
interface IterationLimits {
  readonly vote: number;
  readonly qa: number;
}

/** The caps for this run: caller-supplied where given, the constants otherwise. */
function resolveIterationLimits(options: DevPipelineOptions | undefined): IterationLimits {
  return {
    vote: options?.maxVoteIterations ?? MAX_VOTE_ITERATIONS,
    qa: options?.maxQaIterations ?? MAX_QA_ITERATIONS,
  };
}

/** Pipeline execution mode. */
export type PipelineMode = 'autonomous' | 'harness';

/**
 * Local quality-gate mode (#3356). Controls the pre-ship typecheck/lint/tests/build gate:
 * - 'off' (default): the gate is never run. Safe for repos lacking standard scripts.
 * - 'advisory': the gate runs and its feedback is recorded, but a red gate does
 *   NOT fail the pipeline.
 * - 'blocking': a red gate fails the phase, the same way a blocking security
 *   scan does.
 */
export type QualityGateMode = 'off' | 'advisory' | 'blocking';

/** Options for pipeline execution. */
export interface DevPipelineOptions {
  /** Session ID for checkpoint/resume. Omit for no persistence. */
  readonly sessionId?: string | undefined;
  /** When true, stop after plan+vote and return partial result (#1717). */
  readonly dryRun?: boolean | undefined;
  /**
   * Pipeline mode (#1704):
   * - 'autonomous' (default): full pipeline runs internally
   * - 'harness': stops after decompose, returns tasks for external implementation
   */
  readonly mode?: PipelineMode | undefined;
  /**
   * Local pre-ship quality-gate mode (#3356). Default 'off' so the pipeline
   * never wedges repos that lack standard build/test scripts. See
   * {@link QualityGateMode}. Requires `stages.qualityGate` to be supplied;
   * if the stage is absent the gate is skipped regardless of mode.
   */
  readonly qualityGate?: QualityGateMode | undefined;
  /**
   * Cap on plan→vote rounds (#4939). Omitted uses {@link MAX_VOTE_ITERATIONS}.
   *
   * The MCP tool has advertised `maxVoteIterations` since it shipped — bounds
   * checked, defaulted to 3, described in the generated tool reference — and
   * nothing read it, so setting it changed nothing.
   */
  readonly maxVoteIterations?: number | undefined;
  /** Cap on implement→QA rounds (#4939). Omitted uses {@link MAX_QA_ITERATIONS}. */
  readonly maxQaIterations?: number | undefined;
  /** Optional BeliefMemory for hindsight updates after plan outcomes (#1720). */
  readonly beliefMemory?: IHindsightBeliefMemory | undefined;
  /**
   * Fail-closed guard invoked at the RESEARCH stage — the untrusted-read
   * chokepoint (#3643). The auto-remediation enforce path (#3618) wires this to
   * `CapabilityLedger.assertCapability('untrusted-input')`, so running the
   * untrusted research stage inside the write+secrets IMPLEMENT phase throws
   * (Rule-of-Two). Not called when {@link researchOverride} is set (no untrusted
   * read happens). Default: undefined (no guard — normal pipeline behavior).
   */
  readonly untrustedInputGuard?: (() => void) | undefined;
  /**
   * Pre-seeded research text (#3643). When set, the RESEARCH stage uses this
   * instead of calling `stages.research()` — so the IMPLEMENT phase can run the
   * pipeline plan-only (from the typed RemediationPlan) with NO fresh untrusted
   * read, while {@link untrustedInputGuard} still fail-closes any code path that
   * forgets to seed it.
   */
  readonly researchOverride?: string | undefined;
  /**
   * Content-provenance trust tier ('1'–'4') threaded into the consensus→execute
   * policy snapshot (#3712). Trust here is about the PROVENANCE of the content
   * that reached this run (the goal/research), not the caller's identity. The MCP
   * `run_dev_pipeline` handler and the `run` entry point thread the caller's real
   * `RequestContext.trustTier`; the auto-remediation IMPLEMENT path may pass `'1'`
   * only because #3643's typed RemediationPlan + CapabilityLedger confine
   * untrusted input upstream. **When undefined the seam behaves as before
   * (#3704): the engine defaults the missing tier to untrusted (4), fail-closed.**
   * Absence anywhere = untrusted; never infer a trusted tier from missing context.
   */
  readonly trustTier?: string | undefined;
  /**
   * Durable, hash-chained audit logger (#3710). When supplied (the MCP server
   * threads its single startup `auditLogger`), the consensus→execute policy gate
   * ALSO persists each `policy.evaluated` decision to the immutable store —
   * carrying mode/ruleIds/stageType — so warn-mode soak evidence survives process
   * exit and feeds the tune/readiness loop. MUST be the server's single instance,
   * not a competing FileAuditStorage (shared hash chain). When undefined (pure-CLI
   * path), behavior is unchanged — the in-memory bus emit is the only sink.
   */
  readonly auditLogger?: IAuditLogger | undefined;
}

/**
 * Execute the full multi-agent development pipeline.
 *
 * When `sessionId` is provided, each stage checkpoints to disk. On crash,
 * re-running with the same sessionId resumes from the last completed stage.
 *
 * @param task - High-level task description
 * @param stages - Pluggable stage implementations
 * @param options - Pipeline options (sessionId for checkpoint/resume)
 * @returns Pipeline result with all outputs
 */
export async function runDevPipeline(
  task: string,
  stages: DevPipelineStages,
  options?: DevPipelineOptions
): Promise<DevPipelineResult> {
  const sid = options?.sessionId;
  const prior = sid !== undefined ? loadCheckpointState(sid) : null;

  // Wire TraceWriter for execution replay (#1719)
  const traceWriter = createTraceWriter(sid);

  try {
    return await runDevPipelineInner(task, stages, options, sid, prior);
  } finally {
    await flushTraceWriter(traceWriter);
  }
}

/** Core pipeline logic, separated for trace writer try/finally. */
async function runDevPipelineInner(
  task: string,
  stages: DevPipelineStages,
  options: DevPipelineOptions | undefined,
  sid: string | undefined,
  prior: PipelineCheckpointState | null
): Promise<DevPipelineResult> {
  const { beliefMemory: bm, auditLogger, trustTier } = options ?? {};

  // Phases 1-2: Research + Plan/Vote
  const { planResult, researchMaturity } = await runPlanningPhase(task, stages, prior, options);

  // DRY RUN: stop after plan+vote, return partial result (#1717)
  if (options?.dryRun === true) {
    logger.info('Dry run — stopping after plan+vote');
    return buildDryRunResult(planResult);
  }

  if (planResult.planStatus !== undefined) {
    const result = buildPlanFailureResult(planResult);
    applyPipelineHindsight(bm, task, sid, result);
    return result;
  }

  // CONSENSUS → EXECUTE policy gate (#3704). The legacy dev-pipeline does not
  // run through the graph PipelineRunner, so the #3177 graph gates never fire
  // here — this seam closes that gap. Reuses evaluatePipelinePolicy (no 4th
  // evaluator); emits policy.evaluated BEFORE any throw so blocked runs are
  // audited. WARN by default (block opt-in via NEXUS_POLICY_GATE_MODE).
  enforceConsensusExecutePolicy(sid, trustTier, auditLogger);

  // Phase 3: Decompose
  const tasks = await runOrResumeDecompose(prior, planResult.plan, stages, {
    conditional: planResult.conditional,
    conditions: planResult.conditions,
    caveats: planResult.caveats,
    researchMaturity,
  });
  if (sid !== undefined) saveStageCheckpoint(sid, 'decompose', { type: 'decompose', tasks });

  // HARNESS MODE: stop after decompose, return tasks for external implementation (#1704)
  if (options?.mode === 'harness') {
    logger.info('Harness mode — returning tasks for external implementation');
    return buildHarnessResult(planResult, tasks);
  }

  // Phases 4-5: Implement + Quality Gate + Security
  const result = await runImplSecurityPhase(planResult, tasks, stages, {
    sid,
    qualityGateMode: options?.qualityGate ?? 'off',
    limits: resolveIterationLimits(options),
  });

  // Apply hindsight with actual pipeline outcome (#1720)
  applyPipelineHindsight(bm, task, sid, result);

  return result;
}

/**
 * Enforces policy at the consensus→execute seam (#3704).
 *
 * Sits after the approved plan-vote loop (and the dryRun short-circuit) and
 * before decompose, so a plan that passed consensus is still policy-checked
 * before any execution work begins. This closes the legacy dev-pipeline gap:
 * unlike the graph path (#3177 gates), `runDevPipeline` orchestrates via the
 * `stages` callbacks and never instantiates the graph PipelineRunner — so a
 * dev-pipeline run only ever traverses THIS seam, not the graph gates. The two
 * paths are disjoint, so no double-evaluation guard is needed (#3704 cond. 4).
 *
 * Reuses the #3177 evaluator (`evaluatePipelinePolicy`) rather than forking a
 * new one. The evaluator emits `policy.evaluated` events on the shared pipeline
 * event bus BEFORE returning, so the emit happens BEFORE the block-mode throw
 * below — blocked runs (the ones we most want audited) are never silently lost
 * (#3704 cond. 1).
 *
 * Mode resolves via `getGateEnforcementMode()`: WARN by default, block/off
 * opt-in via `NEXUS_POLICY_GATE_MODE`. The `trustTier` is the content-provenance
 * tier threaded from the caller (#3712) — the MCP handler and `run` entry point
 * pass the real `RequestContext.trustTier`. **When undefined (no caller threaded
 * a tier), the snapshot stays empty, so the engine defaults the missing tier to
 * untrusted (4), fail-closed** — never infer a trusted tier from absence. Under
 * WARN a violation logs + continues (cond. 3); under block it throws
 * {@link PolicyBlockedError} and aborts the run (cond. 2).
 */
function enforceConsensusExecutePolicy(
  sessionId: string | undefined,
  trustTier: string | undefined,
  auditLogger: IAuditLogger | undefined
): void {
  const mode = getGateEnforcementMode();
  if (mode === 'off') return;

  // Build the durable trail ONCE for this gate evaluation (#3710), wrapping the
  // server's single auditLogger. Undefined when none is threaded (pure-CLI path).
  const auditTrail = buildPolicyAuditTrail(auditLogger);

  const context: PolicyContext = {
    taskId: sessionId ?? 'dev-pipeline',
    stageId: 'consensus-to-execute',
    stageType: 'execute',
    // Inline narrowing of the threaded tier into the typed snapshot (#3712):
    // only a real string tier populates it; undefined keeps the snapshot empty
    // so the engine fail-closes to untrusted (4). Absence = untrusted.
    pipelineState: typeof trustTier === 'string' ? { trustTier } : {},
  };

  // evaluatePipelinePolicy emits policy.evaluated on the shared bus BEFORE it
  // returns — so the emit precedes the throw below (#3704 cond. 1). When a
  // durable trail is wired (#3710), it ALSO appends one hash-chained
  // policy_gate record per violation (dual-emit) carrying mode/ruleIds/stageType.
  const result = evaluatePipelinePolicy(
    {
      engine: createDefaultPolicyEngine(),
      mode,
      eventBus: getPipelineEventBus(),
      ...(auditTrail !== undefined ? { auditTrail } : {}),
    },
    context
  );

  // Decision 1 = THROW (not the graceful completed:false path) so a policy
  // denial aborts the run, consistent with the graph gate path (#3704 cond. 2).
  if (mode === 'block' && !result.allowed) {
    throw new PolicyBlockedError(context.stageId, result.violations);
  }
}

/**
 * Build the durable policy AuditTrail ONCE per run (#3710). Wraps the SERVER's
 * single `auditLogger` in a durable sink so the consensus→execute gate's
 * `policy.evaluated` decisions are persisted to the shared hash chain — NOT a
 * competing FileAuditStorage (chain integrity). Returns undefined when no logger
 * is threaded (pure-CLI path), so that path stays byte-identical to before.
 *
 * Serialization (#3710 condition 2): the returned trail's `append` calls
 * `auditLogger.log()` synchronously (no await between hash assignment and queue
 * push), so concurrent dev-pipeline runs sharing one logger serialize their
 * chain writes on the single-threaded event loop — `verifyChain()` stays valid.
 */
function buildPolicyAuditTrail(auditLogger: IAuditLogger | undefined): AuditTrail | undefined {
  if (auditLogger === undefined) return undefined;
  return createAuditTrail(createDurableAuditSink(auditLogger));
}

/** Create a TraceWriter when sessionId is available (#1719). */
function createTraceWriter(sessionId: string | undefined): TraceWriter | null {
  if (sessionId === undefined) return null;
  try {
    const tracesDir = nexusDataPath('traces');
    return new TraceWriter(getPipelineEventBus(), {
      runsDir: tracesDir,
      runId: `pipeline-${sessionId}`,
    });
  } catch (error: unknown) {
    logger.warn('Failed to create TraceWriter', { error: String(error) });
    return null;
  }
}

/** Flush and stop the TraceWriter, swallowing errors. */
async function flushTraceWriter(writer: TraceWriter | null): Promise<void> {
  if (writer === null) return;
  try {
    await writer.flush();
  } catch (error: unknown) {
    logger.warn('Failed to flush execution trace', { error: String(error) });
  } finally {
    writer.stop();
  }
}

/** Build a partial result for dry-run mode. */
function buildDryRunResult(planResult: PlanVoteResult): DevPipelineResult {
  return {
    completed: false,
    // Says WHY completion is false: by request, not by fault.
    dryRun: true,
    plan: planResult.plan,
    tasks: [],
    voteIterations: planResult.iterations,
    qaIterations: 0,
    // #4772: a dry run stops before security by design. `false` alone read as
    // "the security gate rejected this"; `securityRan: false` says it never ran.
    securityPassed: false,
    securityRan: false,
    ...(planResult.planStatus !== undefined ? { planStatus: planResult.planStatus } : {}),
    ...(planResult.planVoteReason !== undefined
      ? { planVoteReason: planResult.planVoteReason }
      : {}),
    ...(planResult.planVoteApprovalPercentage !== undefined
      ? { planVoteApprovalPercentage: planResult.planVoteApprovalPercentage }
      : {}),
    ...(planResult.planVoteFeedback !== undefined
      ? { planVoteFeedback: planResult.planVoteFeedback }
      : {}),
  };
}

/** Build a terminal planning result that cannot enter decompose/implement. */
function buildPlanFailureResult(planResult: PlanVoteResult): DevPipelineResult {
  return {
    completed: false,
    plan: planResult.plan,
    tasks: [],
    voteIterations: planResult.iterations,
    qaIterations: 0,
    securityPassed: false,
    securityRan: false,
    planStatus: planResult.planStatus ?? 'unapproved',
    ...(planResult.planVoteReason !== undefined
      ? { planVoteReason: planResult.planVoteReason }
      : {}),
    ...(planResult.planVoteApprovalPercentage !== undefined
      ? { planVoteApprovalPercentage: planResult.planVoteApprovalPercentage }
      : {}),
    ...(planResult.planVoteFeedback !== undefined
      ? { planVoteFeedback: planResult.planVoteFeedback }
      : {}),
  };
}

/** Phases 1-2: Research + Plan/Vote with checkpoint support. */
/**
 * Resolve the RESEARCH stage output (#3643). When `researchOverride` is set, use
 * it plan-only (no untrusted read). Otherwise the `untrustedInputGuard` is the
 * fail-closed chokepoint: it runs immediately before the untrusted research
 * stage, so an active IMPLEMENT-phase ledger throws rather than read.
 */
async function resolveResearch(
  prior: PipelineCheckpointState | null,
  task: string,
  stages: DevPipelineStages,
  options: DevPipelineOptions | undefined
): Promise<ResearchContext> {
  // #3234: the checkpoint persists research as text only, so a RESUMED run has no
  // structured metadata — wrap the text with empty metadata (degrades cleanly).
  if (prior?.research !== undefined) {
    logger.info('Resuming from checkpoint', { stage: 'research' });
    return researchContextFromText(prior.research);
  }
  const override = options?.researchOverride;
  if (override !== undefined) {
    return researchContextFromText(override);
  }
  options?.untrustedInputGuard?.();
  return withStep({ name: 'research', attrs: { task: task.slice(0, 100) } }, async (ctx) => {
    const rc = await stages.research(task);
    ctx.setSummary(`${String(rc.text.length)} chars`);
    return rc;
  });
}

async function runPlanningPhase(
  task: string,
  stages: DevPipelineStages,
  prior: PipelineCheckpointState | null,
  options: DevPipelineOptions | undefined
): Promise<{
  planResult: PlanVoteResult;
  /** #3234: research-maturity of this run, attached to decomposed tasks. */
  researchMaturity: number;
}> {
  const sid = options?.sessionId;
  const bm = options?.beliefMemory;
  const research = await resolveResearch(prior, task, stages, options);
  // #3234: a deterministic research-maturity score (RECORD + measure; see #3815
  // for the gated live-routing use). Fresh-run scoped — a resumed run has empty
  // metadata → 0, degrading cleanly.
  const researchMaturity = deriveResearchMaturity(research.metadata);
  // #3234: persist text only (unchanged checkpoint shape — metadata is fresh-run
  // scoped and not resumable). plan/vote consume the text via research.text.
  if (sid !== undefined) {
    saveStageCheckpoint(sid, 'research', { type: 'research', text: research.text });
  }

  const planContext = await assemblePlanContext(research.text, task, sid, bm);
  const planResult = await runPlanOrResume(prior, task, planContext, stages, {
    sid,
    limits: resolveIterationLimits(options),
  });
  if (sid !== undefined && planResult.planStatus === undefined) {
    saveStageCheckpoint(sid, 'plan', {
      type: 'plan',
      text: planResult.plan,
      iterations: planResult.iterations,
    });
    saveStageCheckpoint(sid, 'vote', {
      type: 'vote',
      approved: true,
      conditional: planResult.conditional,
      conditions: planResult.conditions,
      caveats: planResult.caveats,
      iterations: planResult.iterations,
    });
  }
  return { planResult, researchMaturity };
}

/** Build result for harness mode — tasks returned for external implementation. */
function buildHarnessResult(planResult: PlanVoteResult, tasks: PipelineTask[]): DevPipelineResult {
  return {
    completed: false,
    // Says WHY completion is false: by request, not by fault (#5888) — the
    // same role `dryRun` plays for the other deliberate stop.
    harnessMode: true,
    plan: planResult.plan,
    tasks,
    voteIterations: planResult.iterations,
    qaIterations: 0,
    // Harness mode hands the tasks back for someone else to implement, so the
    // scan never ran. `securityPassed: false` alone would read as a rejection.
    securityPassed: false,
    securityRan: false,
  };
}

/**
 * Aggregate completion status of planned tasks (#5645).
 * A plan with zero tasks completes nothing (whenEmpty: false).
 */
function deriveTaskAggregate(
  plannedTasks: readonly PipelineTask[],
  completedTasks: readonly PipelineTask[]
): {
  readonly allTasksDone: boolean;
  readonly taskStatus: 'all_done' | 'partial' | 'none';
} {
  const completedById = new Map(completedTasks.map((t) => [t.id, t]));
  const allTasksDone = allOf(
    plannedTasks,
    (task) => completedById.get(task.id)?.status === 'done',
    false
  );
  const anyTaskDone = anyOf(
    plannedTasks,
    (task) => completedById.get(task.id)?.status === 'done',
    false
  );
  return {
    allTasksDone,
    taskStatus: allTasksDone ? 'all_done' : anyTaskDone ? 'partial' : 'none',
  };
}

/** Phases 4-5: Implement/QA + Quality Gate + Security with checkpoint support. */
async function runImplSecurityPhase(
  planResult: { plan: string; iterations: number },
  tasks: PipelineTask[],
  stages: DevPipelineStages,
  run: { sid: string | undefined; qualityGateMode: QualityGateMode; limits: IterationLimits }
): Promise<DevPipelineResult> {
  const { sid, qualityGateMode, limits } = run;
  const implResult = await implementQaLoop(tasks, stages, limits);
  if (sid !== undefined)
    saveStageCheckpoint(sid, 'implement', { type: 'implement', tasks: implResult.completedTasks });

  const { allTasksDone, taskStatus } = deriveTaskAggregate(tasks, implResult.completedTasks);

  // Local pre-ship quality gate (#3356). In 'blocking' mode a red gate fails
  // the phase before the security scan even runs — same posture as a blocking
  // security finding. In 'advisory' mode we record feedback but never fail.
  const qaGate = await runQualityGateStage(stages, qualityGateMode);
  if (qualityGateMode === 'blocking' && !qaGate.passed) {
    return {
      completed: false,
      plan: planResult.plan,
      tasks: implResult.completedTasks.length > 0 ? implResult.completedTasks : tasks,
      voteIterations: planResult.iterations,
      qaIterations: implResult.totalIterations,
      // The gate short-circuited before the scan — absence, not a verdict.
      securityPassed: false,
      securityRan: false,
      taskStatus,
    };
  }

  const security = await withStep({ name: 'security-scan' }, async (ctx) => {
    const r = await stages.securityScan();
    ctx.setSummary(r.passed ? 'passed' : 'FAILED');
    return r;
  });
  if (sid !== undefined) {
    saveStageCheckpoint(sid, 'security', { type: 'security', passed: security.passed });
    if (security.passed) cleanupCheckpoint(sid);
  }

  return {
    completed: allTasksDone && security.passed,
    plan: planResult.plan,
    tasks: implResult.completedTasks.length > 0 ? implResult.completedTasks : tasks,
    voteIterations: planResult.iterations,
    qaIterations: implResult.totalIterations,
    securityPassed: security.passed,
    securityRan: security.verdict !== 'skip',
    taskStatus,
    ...(security.verdict === 'skip' ? { securityNote: security.feedback } : {}),
  };
}

/** Result of the optional pre-ship quality gate (#3356). */
interface QualityGateOutcome {
  readonly passed: boolean;
  readonly feedback: string;
}

/**
 * Run the local quality gate (#3356) when enabled and supplied.
 *
 * Skips entirely when mode is 'off' or `stages.qualityGate` is absent — the
 * gate must never wedge repos that lack standard build/test scripts. Wraps the
 * call in `withStep` so it participates in the same EventBus/trace plumbing the
 * security scan uses. In 'advisory' mode a red gate is reported but treated as a
 * non-blocking outcome by the caller.
 */
async function runQualityGateStage(
  stages: DevPipelineStages,
  mode: QualityGateMode
): Promise<QualityGateOutcome> {
  if (mode === 'off' || stages.qualityGate === undefined) {
    return { passed: true, feedback: 'Quality gate skipped' };
  }
  const runGate = stages.qualityGate.bind(stages);
  return withStep({ name: 'quality-gate', attrs: { mode } }, async (ctx) => {
    const r = await runGate();
    const advisory = mode === 'advisory' && !r.passed;
    ctx.setSummary(r.passed ? 'passed' : advisory ? 'FAILED (advisory)' : 'FAILED');
    if (advisory) {
      logger.warn('Quality gate failed (advisory — not blocking)', {
        feedback: r.feedback.slice(0, 200),
      });
    }
    return r;
  });
}

/** Run plan/vote or return from checkpoint. */
async function runPlanOrResume(
  prior: PipelineCheckpointState | null,
  task: string,
  research: string,
  stages: DevPipelineStages,
  run: { sid: string | undefined; limits: IterationLimits }
): Promise<PlanVoteResult> {
  const { sid: sessionId, limits } = run;
  if (prior?.plan !== undefined) {
    logger.info('Resuming from checkpoint', { stage: 'plan', sessionId });
    return {
      plan: prior.plan,
      iterations: prior.voteIterations ?? 0,
      conditional: prior.voteConditional ?? false,
      conditions: prior.voteConditions ?? [],
      caveats: prior.voteCaveats ?? [],
    };
  }
  return planVoteLoop(task, research, stages, sessionId, limits);
}

/** Conditional vote metadata for task annotation. */
interface ConditionalMeta {
  readonly conditional: boolean;
  readonly conditions: readonly string[];
  readonly caveats: readonly string[];
  /** #3234: research-maturity of the run, attached to each fresh task. */
  readonly researchMaturity?: number | undefined;
}

/** Result of the plan/revision loop, including terminal gate evidence. */
interface PlanVoteResult extends ConditionalMeta {
  readonly plan: string;
  readonly iterations: number;
  readonly planStatus?: 'empty' | 'no_quorum' | 'unapproved';
  readonly planVoteReason?: string;
  readonly planVoteApprovalPercentage?: number;
  readonly planVoteFeedback?: string;
}

/** Run decompose or return from checkpoint. */
async function runOrResumeDecompose(
  prior: PipelineCheckpointState | null,
  plan: string,
  stages: DevPipelineStages,
  meta: ConditionalMeta
): Promise<PipelineTask[]> {
  if (prior?.tasks !== undefined) {
    logger.info('Resuming from checkpoint', { stage: 'decompose' });
    return [...prior.tasks];
  }
  const tasks = await withStep({ name: 'decompose' }, async (ctx) => {
    const r = await stages.decompose(plan);
    ctx.setSummary(`${String(r.length)} tasks`);
    return r;
  });
  // #3234: attach research-maturity to every FRESH task (the resume path above
  // returns prior.tasks untouched, preserving the original maturity). Conditional
  // fields are added only on a conditional_go vote, as before.
  return tasks.map((t) => ({
    ...t,
    ...(meta.conditional ? { conditions: meta.conditions, caveats: meta.caveats } : {}),
    ...(meta.researchMaturity !== undefined ? { researchMaturity: meta.researchMaturity } : {}),
  }));
}

/** Extract conditional metadata from an approved vote. */
function extractConditionalMeta(vote: VoteResult): ConditionalMeta {
  if (vote.kind === 'conditional_go') {
    return { conditional: true, conditions: vote.conditions, caveats: vote.caveats };
  }
  return { conditional: false, conditions: [], caveats: [] };
}

/**
 * Plan → Vote → iterate on feedback until approved or exhausted.
 *
 * Uses DevPipelineStages.vote() for each round (preserves progress
 * callbacks, outcome recording, event emission from agent-executor).
 * The iteration pattern matches runIterativeConsensus (#1734).
 */
async function planVoteLoop(
  task: string,
  research: string,
  stages: DevPipelineStages,
  sessionId: string | undefined,
  limits: IterationLimits
): Promise<PlanVoteResult> {
  let feedback: string | undefined;
  let plan = '';
  let lastRejected: Extract<VoteResult, { kind: 'rejected' }> | undefined;

  for (let i = 1; i <= limits.vote; i++) {
    plan = await withStep({ name: `plan (i=${String(i)})`, attrs: { iteration: i } }, () =>
      stages.plan(task, research, feedback)
    );

    // #4772: the planner produced nothing. Voting on an empty plan wastes a
    // panel and yields a verdict about no proposal, so stop and let the caller
    // see `planStatus: 'empty'` instead of a plausible-looking result.
    if (plan.trim() === '') {
      logger.warn('Planner returned no plan — stopping before vote', { iteration: i, sessionId });
      return {
        plan: '',
        iterations: i,
        conditional: false,
        conditions: [],
        caveats: [],
        planStatus: 'empty',
      };
    }

    const voteOutcome = await retryNoQuorumVote(
      () => runPlanVote(plan, research, stages, i),
      DEFAULT_MAX_NO_QUORUM_RETRIES,
      (attempt, vote) => {
        logQuorumRetry(attempt, vote, sessionId);
      }
    );
    const vote = voteOutcome.vote;

    // Closes #2963 site 4: include sessionId so plan-loop post-mortems
    // can correlate to checkpointed sessions on disk. The variable
    // was already in scope at the caller (#dev-pipeline runDevPipeline);
    // threaded through runPlanOrResume → planVoteLoop here.
    if (vote.kind === 'approved' || vote.kind === 'conditional_go') {
      return buildApprovedPlanResult(plan, i, vote, sessionId);
    }

    if (vote.kind === 'no_quorum') {
      return buildNoQuorumPlanResult(plan, i, vote, voteOutcome.retries);
    }

    lastRejected = vote;
    feedback = vote.feedback;
    logger.warn('Plan rejected, iterating', {
      iteration: i,
      feedback: feedback.slice(0, 200),
      sessionId,
    });
  }

  return buildUnapprovedPlanResult(plan, limits.vote, lastRejected, sessionId);
}

function buildApprovedPlanResult(
  plan: string,
  iterations: number,
  vote: Exclude<VoteResult, { kind: 'rejected' | 'no_quorum' }>,
  sessionId: string | undefined
): PlanVoteResult {
  const meta = extractConditionalMeta(vote);
  logger.info('Plan approved', {
    iteration: iterations,
    approval: vote.approvalPercentage,
    sessionId,
    ...meta,
  });
  return { plan, iterations, ...meta };
}

/** Run one stage-aware plan vote so progress/outcome instrumentation remains intact. */
async function runPlanVote(
  plan: string,
  research: string,
  stages: DevPipelineStages,
  iteration: number
): Promise<VoteResult> {
  return withStep({ name: `vote (i=${String(iteration)})`, attrs: { iteration } }, async (ctx) => {
    const result = await stages.vote(plan, research);
    const label =
      result.kind === 'no_quorum' ? 'no_quorum' : isApproved(result) ? 'approved' : 'rejected';
    ctx.setSummary(`${String(Math.round(result.approvalPercentage))}% ${label}`);
    return result;
  });
}

function logQuorumRetry(
  attempt: number,
  vote: Extract<VoteResult, { kind: 'no_quorum' }>,
  sessionId: string | undefined
): void {
  logger.warn('Plan vote reached no_quorum — re-running the same plan', {
    attempt,
    maxNoQuorumRetries: DEFAULT_MAX_NO_QUORUM_RETRIES,
    reason: vote.reason,
    sessionId,
  });
}

function buildNoQuorumPlanResult(
  plan: string,
  iterations: number,
  vote: Extract<VoteResult, { kind: 'no_quorum' }>,
  retries: number
): PlanVoteResult {
  logger.warn('Plan vote could not reach quorum — stopping', {
    retries,
    reason: vote.reason,
  });
  return {
    plan,
    iterations,
    conditional: false,
    conditions: [],
    caveats: [],
    planStatus: 'no_quorum',
    planVoteReason: vote.reason,
    planVoteApprovalPercentage: vote.approvalPercentage,
  };
}

function buildUnapprovedPlanResult(
  plan: string,
  iterations: number,
  vote: Extract<VoteResult, { kind: 'rejected' }> | undefined,
  sessionId: string | undefined
): PlanVoteResult {
  const feedback = vote?.feedback ?? 'No plan vote was run';
  const approvalPercentage = vote?.approvalPercentage ?? 0;
  logger.warn('Max vote iterations reached without plan approval — stopping', {
    sessionId,
    iterations,
    approvalPercentage,
    feedback: feedback.slice(0, 200),
  });
  return {
    plan,
    iterations,
    conditional: false,
    conditions: [],
    caveats: [],
    planStatus: 'unapproved',
    planVoteApprovalPercentage: approvalPercentage,
    planVoteFeedback: feedback,
  };
}

/** Result of implementing a single task. */
interface TaskImplResult {
  readonly iterations: number;
  readonly task: PipelineTask;
}

/** Implement a single task with QA iteration loop via reusable runQaLoop (#1707). */
async function implementSingleTask(
  task: PipelineTask,
  stages: DevPipelineStages,
  limits: IterationLimits
): Promise<TaskImplResult> {
  let currentTask: PipelineTask = { ...task, status: 'in_progress' };
  const qaResult = await runQaLoop<string>(
    async (feedback) => {
      if (feedback !== undefined) {
        currentTask = {
          id: task.id,
          title: task.title,
          description: task.description,
          assignedTo: task.assignedTo,
          status: 'rejected',
          feedback,
          // #3234: preserve research-maturity across the rejection reconstruction.
          researchMaturity: task.researchMaturity,
        };
      }
      return stages.implement(currentTask);
    },
    async (impl) => {
      const review = await stages.qaReview(currentTask, impl);
      return { verdict: review.verdict, feedback: review.feedback, issues: review.issues };
    },
    limits.qa
  );
  const finalTask: PipelineTask = {
    id: task.id,
    title: task.title,
    description: task.description,
    assignedTo: task.assignedTo,
    status: qaResult.approved ? 'done' : 'rejected',
    implementation: qaResult.output,
    feedback: qaResult.feedback,
    // #3234: preserve research-maturity through to the final task.
    researchMaturity: task.researchMaturity,
  };
  return { iterations: qaResult.iterations, task: finalTask };
}

/** Result of the implement+QA loop. */
interface ImplLoopResult {
  readonly totalIterations: number;
  readonly completedTasks: readonly PipelineTask[];
}

/** Max concurrent task implementations per wave (#1734 Phase 1.3). */
const MAX_IMPL_CONCURRENCY = 4;

/** Implement tasks with bounded-concurrency parallel dispatch (#1695, #1734). */
async function implementQaLoop(
  tasks: PipelineTask[],
  stages: DevPipelineStages,
  limits: IterationLimits
): Promise<ImplLoopResult> {
  if (tasks.length === 0) return { totalIterations: 0, completedTasks: [] };

  const taskFns = tasks.map((task) => () => implementSingleTaskSafe(task, stages, limits));
  const results = await executeWithConcurrency(taskFns, MAX_IMPL_CONCURRENCY);
  return aggregateImplResults(results);
}

/** Execute a task with error handling, returning a safe result. */
async function implementSingleTaskSafe(
  task: PipelineTask,
  stages: DevPipelineStages,
  limits: IterationLimits
): Promise<TaskImplResult | null> {
  try {
    return await implementSingleTask(task, stages, limits);
  } catch (error) {
    const reason = error instanceof Error ? error : new Error(String(error));
    logger.error('Task implementation failed', reason, {});
    return null;
  }
}

/** Aggregate implementation results into totals. */
function aggregateImplResults(results: ReadonlyArray<TaskImplResult | null>): ImplLoopResult {
  let totalIterations = 0;
  const completedTasks: PipelineTask[] = [];
  for (const r of results) {
    if (r !== null) {
      totalIterations += r.iterations;
      completedTasks.push(r.task);
    } else {
      totalIterations++;
    }
  }
  return { totalIterations, completedTasks };
}

/** Execute async tasks with bounded concurrency (#1734). */
async function executeWithConcurrency<T>(
  tasks: ReadonlyArray<() => Promise<T>>,
  limit: number
): Promise<T[]> {
  const results: T[] = [];
  let nextIndex = 0;
  async function runNext(): Promise<void> {
    while (nextIndex < tasks.length) {
      const idx = nextIndex;
      nextIndex++;
      const task = tasks[idx];
      if (task !== undefined) results[idx] = await task();
    }
  }
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, () => runNext());
  await Promise.all(workers);
  return results;
}
